/**
 * Client for the feat-342 Recording Activity report (Bespoke admin only).
 *
 * Contract: worktrees/feat-342-report-mic-activity/CONTRACT.md —
 * `GET /api/admin/recording-activity?tenant_id=<uuid>`, `Depends(require_admin)`.
 *
 * The BACKEND computes every age, every count and the status ladder (one source
 * of truth). This module only coerces types at the edge, groups rows by location,
 * counts statuses for the section summary line, sorts, and formats. It never
 * derives a status, and a missing value stays `null` (rendered `—`) — never 0 and
 * never "healthy" (the feat-274 lesson).
 *
 * Fake mode (`npm run start:fake` / Jest): returns canned rows covering every
 * status so the page renders without a backend.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';
import { fakeListTenants } from './fakeTenantData';
// Same duration vocabulary as the Live Servers chips (design/NOTES.md).
import { formatSeconds } from '../components/restaurant/deviceHealth';

// ---------- Types (mirror CONTRACT.md r1 + r2 additions) ----------

/**
 * `never_ended` (r2): the DB shift is still open (`is_open` true) but nothing has
 * arrived for longer than `thresholds.never_ended_after_seconds` — the crew member
 * never tapped "End shift". The backend decides it; it outranks red/amber.
 */
export type RecordingStatus = 'red' | 'amber' | 'green' | 'never_ended' | 'ended';

export interface RecordingActivityRow {
  location_id: string;
  location_name: string;
  server_id: string;
  server_name: string;
  shift_id: string;
  /** Stored location-local DATETIME, ISO without offset. Displayed as-is. */
  shift_started_local: string;
  shift_ended_local: string | null;
  is_open: boolean;
  end_reason: string | null;
  shift_length_seconds: number | null;
  audio_seconds: number;
  transcript_part_count: number;
  last_audio_age_seconds: number | null;
  last_transcript_age_seconds: number | null;
  last_heartbeat_age_seconds: number | null;
  mic_route: string | null;
  battery_level: number | null;
  ws_reconnect_count: number | null;
  device_model: string | null;
  app_version: string | null;
  patch_number: number | null;
  status: RecordingStatus;
  status_reason: string | null;
  /** r2: location-local ISO (no offset) of MAX(last chunk, heartbeat, transcript part); null if none. */
  last_activity_local: string | null;
  /** r2: end − last X, only for `ended` / `never_ended` rows; null for open rows or when X never happened. */
  last_audio_before_end_seconds: number | null;
  last_transcript_before_end_seconds: number | null;
  last_heartbeat_before_end_seconds: number | null;
}

export interface RecordingActivityThresholds {
  red_no_audio_seconds: number;
  red_no_heartbeat_seconds: number;
  amber_no_transcript_seconds: number;
  /** r2. null when an older backend omits it — the key then leaves the entry out. */
  never_ended_after_seconds: number | null;
}

export interface RecordingActivityResponse {
  generated_at: string;
  tenant_id: string;
  window_days: number;
  thresholds: RecordingActivityThresholds;
  rows: RecordingActivityRow[];
}

export interface TenantOption {
  tenant_id: string;
  tenant_name: string;
}

// ---------- HTTP ----------

const isFake = (): boolean => config.environment === 'fake';

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

async function apiGet<T>(path: string): Promise<T> {
  const resp = await authedApiFetch(backendUrl(path), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} ${resp.status}: ${text}`);
  }
  return resp.json();
}

// DECIMAL columns may arrive as strings — coerce at the edge, keep null as null.
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};
const num0 = (v: unknown): number => numOrNull(v) ?? 0;
const strOrNull = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);

const STATUSES: RecordingStatus[] = ['red', 'amber', 'green', 'never_ended', 'ended'];

export function normalizeRow(r: Record<string, unknown>): RecordingActivityRow {
  const status = STATUSES.includes(r.status as RecordingStatus)
    ? (r.status as RecordingStatus)
    : // An unknown status is rendered as the backend sent it would be ideal, but the
      // type is closed; fall back to 'ended' (grey) only when is_open is false,
      // otherwise 'red' so an unrecognised open state is never shown as healthy.
      r.is_open
      ? 'red'
      : 'ended';
  return {
    location_id: String(r.location_id ?? ''),
    location_name: String(r.location_name ?? ''),
    server_id: String(r.server_id ?? ''),
    server_name: String(r.server_name ?? ''),
    shift_id: String(r.shift_id ?? ''),
    shift_started_local: String(r.shift_started_local ?? ''),
    shift_ended_local: strOrNull(r.shift_ended_local),
    is_open: Boolean(r.is_open),
    end_reason: strOrNull(r.end_reason),
    shift_length_seconds: numOrNull(r.shift_length_seconds),
    audio_seconds: num0(r.audio_seconds),
    transcript_part_count: num0(r.transcript_part_count),
    last_audio_age_seconds: numOrNull(r.last_audio_age_seconds),
    last_transcript_age_seconds: numOrNull(r.last_transcript_age_seconds),
    last_heartbeat_age_seconds: numOrNull(r.last_heartbeat_age_seconds),
    mic_route: strOrNull(r.mic_route),
    battery_level: numOrNull(r.battery_level),
    ws_reconnect_count: numOrNull(r.ws_reconnect_count),
    device_model: strOrNull(r.device_model),
    app_version: strOrNull(r.app_version),
    patch_number: numOrNull(r.patch_number),
    status,
    status_reason: strOrNull(r.status_reason),
    last_activity_local: strOrNull(r.last_activity_local),
    last_audio_before_end_seconds: numOrNull(r.last_audio_before_end_seconds),
    last_transcript_before_end_seconds: numOrNull(r.last_transcript_before_end_seconds),
    last_heartbeat_before_end_seconds: numOrNull(r.last_heartbeat_before_end_seconds),
  };
}

export async function getRecordingActivity(tenantId: string): Promise<RecordingActivityResponse> {
  if (isFake()) return fakeRecordingActivity(tenantId);
  const raw = await apiGet<Record<string, unknown>>(
    `/api/admin/recording-activity?tenant_id=${encodeURIComponent(tenantId)}`,
  );
  const th = (raw.thresholds ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(raw.rows) ? (raw.rows as Array<Record<string, unknown>>) : [];
  return {
    generated_at: String(raw.generated_at ?? ''),
    tenant_id: String(raw.tenant_id ?? tenantId),
    window_days: num0(raw.window_days),
    thresholds: {
      red_no_audio_seconds: num0(th.red_no_audio_seconds),
      red_no_heartbeat_seconds: num0(th.red_no_heartbeat_seconds),
      amber_no_transcript_seconds: num0(th.amber_no_transcript_seconds),
      never_ended_after_seconds: numOrNull(th.never_ended_after_seconds),
    },
    rows: rows.map(normalizeRow),
  };
}

/** Tenant list for the picker — same `/api/tenant` route the other admin pages use. */
export async function listTenantsForPicker(): Promise<TenantOption[]> {
  if (isFake()) {
    return fakeListTenants().map((t) => ({ tenant_id: t.tenant_id, tenant_name: t.tenant_name }));
  }
  const raw = await apiGet<unknown>('/api/tenant');
  // Tolerate both a bare array and `{ tenants: [...] }`.
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { tenants?: unknown[] })?.tenants)
      ? (raw as { tenants: unknown[] }).tenants
      : [];
  return (list as Array<Record<string, unknown>>).map((t) => ({
    tenant_id: String(t.tenant_id ?? ''),
    tenant_name: String(t.tenant_name ?? t.tenant_id ?? ''),
  }));
}

// ---------- Pure helpers ----------

/** Default tenant: exact "Ladle & Leaf" first, then any name containing "ladle", else the first. */
export function pickDefaultTenant(tenants: TenantOption[]): TenantOption | null {
  if (!tenants.length) return null;
  const exact = tenants.find((t) => t.tenant_name.trim().toLowerCase() === 'ladle & leaf');
  if (exact) return exact;
  const ladle = tenants.find((t) => t.tenant_name.toLowerCase().includes('ladle'));
  return ladle ?? tenants[0];
}

export interface LocationGroup {
  location_id: string;
  location_name: string;
  rows: RecordingActivityRow[];
}

/** One group per location, alphabetical by name (case-insensitive). Row order is preserved. */
export function groupByLocation(rows: RecordingActivityRow[]): LocationGroup[] {
  const map = new Map<string, LocationGroup>();
  rows.forEach((r) => {
    const key = r.location_id || r.location_name;
    let g = map.get(key);
    if (!g) {
      g = { location_id: r.location_id, location_name: r.location_name, rows: [] };
      map.set(key, g);
    }
    g.rows.push(r);
  });
  return Array.from(map.values()).sort((a, b) =>
    a.location_name.localeCompare(b.location_name, undefined, { sensitivity: 'base' }),
  );
}

export interface SectionSummary {
  people: number;
  /**
   * Open shifts that are not `never_ended` (r2) — the backend's is_open and
   * status, not a client inference. A never-ended shift is open in the DB but is
   * not recording anything.
   */
  recording: number;
  red: number;
  amber: number;
  green: number;
  never_ended: number;
  ended: number;
}

export function summarize(rows: RecordingActivityRow[]): SectionSummary {
  const s: SectionSummary = {
    people: rows.length,
    recording: 0,
    red: 0,
    amber: 0,
    green: 0,
    never_ended: 0,
    ended: 0,
  };
  rows.forEach((r) => {
    s[r.status] += 1;
    if (r.is_open && r.status !== 'never_ended') s.recording += 1;
  });
  return s;
}

/** `5 people · 3 recording · 1 red · 1 amber · 0 never ended` — every term always present (stable width). */
export function summaryLine(s: SectionSummary): string {
  return `${s.people} ${s.people === 1 ? 'person' : 'people'} · ${s.recording} recording · ${s.red} red · ${s.amber} amber · ${s.never_ended} never ended`;
}

// ----- Sorting -----

export type SortKey =
  | 'status'
  | 'server_name'
  | 'shift_started_local'
  | 'shift_status'
  | 'shift_length_seconds'
  | 'audio_seconds'
  | 'transcript_part_count'
  | 'last_audio_age_seconds'
  | 'last_transcript_age_seconds'
  | 'last_heartbeat_age_seconds'
  | 'mic_route'
  | 'battery_level'
  | 'ws_reconnect_count'
  | 'device';

export type SortDir = 'asc' | 'desc';

export interface SortState {
  key: SortKey;
  dir: SortDir;
}

/**
 * Default: Status ▼ = worst first (red → amber → green → never_ended → ended), then
 * the Last audio column's displayed value desc. Status sorts on SEVERITY, so "descending" reads as "most severe first"
 * (design/01-recording-activity.html shows Status ▼ active by default).
 */
export const DEFAULT_SORT: SortState = { key: 'status', dir: 'desc' };

/** Presentation-only ordering of the backend's status — higher = more severe. */
export const STATUS_SEVERITY: Record<RecordingStatus, number> = {
  red: 4,
  amber: 3,
  green: 2,
  never_ended: 1,
  ended: 0,
};

/**
 * r2: `ended` and `never_ended` rows are "closed" for display — the Last audio /
 * transcript / heartbeat columns show the backend's gap before the end instead of
 * the age. Keyed on status (the backend's call), not on is_open, because a
 * never-ended shift is still open in the DB.
 */
export const isClosedForDisplay = (r: Pick<RecordingActivityRow, 'status'>): boolean =>
  r.status === 'ended' || r.status === 'never_ended';

/**
 * When a never-ended shift went quiet, location-local: last activity, or the shift
 * start when nothing ever arrived (`last_activity_local` null). Same fallback the
 * backend uses for the "Went quiet …" reason (CONTRACT r2), so the cell and the
 * chip agree.
 */
export const quietSinceLocal = (
  r: Pick<RecordingActivityRow, 'last_activity_local' | 'shift_started_local'>,
): string | null => r.last_activity_local ?? (r.shift_started_local || null);

export type LastKind = 'audio' | 'transcript' | 'heartbeat';

/** The value the Last X column displays (and sorts on): before-end seconds for closed rows, age for open rows. */
export function lastValue(r: RecordingActivityRow, kind: LastKind): number | null {
  if (isClosedForDisplay(r)) {
    return kind === 'audio'
      ? r.last_audio_before_end_seconds
      : kind === 'transcript'
        ? r.last_transcript_before_end_seconds
        : r.last_heartbeat_before_end_seconds;
  }
  return kind === 'audio'
    ? r.last_audio_age_seconds
    : kind === 'transcript'
      ? r.last_transcript_age_seconds
      : r.last_heartbeat_age_seconds;
}

/** Battery normalised to a 0–100 scale for sorting only (display uses formatBattery). */
const batteryPct = (v: number | null): number | null => (v === null ? null : v <= 1 ? v * 100 : v);

function sortValue(r: RecordingActivityRow, key: SortKey): string | number | null {
  switch (key) {
    case 'status':
      return STATUS_SEVERITY[r.status];
    case 'server_name':
      return r.server_name || null;
    case 'shift_started_local':
      return r.shift_started_local || null;
    case 'shift_status':
      // Ascending: open shifts first, then never-ended by last activity, then ended by end time.
      if (r.status === 'never_ended') return `1${quietSinceLocal(r) ?? ''}`;
      return r.is_open ? '0' : `2${r.shift_ended_local ?? ''}`;
    case 'last_audio_age_seconds':
      return lastValue(r, 'audio');
    case 'last_transcript_age_seconds':
      return lastValue(r, 'transcript');
    case 'last_heartbeat_age_seconds':
      return lastValue(r, 'heartbeat');
    case 'battery_level':
      return batteryPct(r.battery_level);
    case 'device': {
      const s = [r.device_model, r.app_version, r.patch_number].filter((x) => x !== null).join(' ');
      return s || null;
    }
    default:
      return r[key] as string | number | null;
  }
}

function cmpValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

/** Blank (null) values always sort last, in either direction. */
function cmpKey(a: RecordingActivityRow, b: RecordingActivityRow, key: SortKey, dir: SortDir): number {
  const va = sortValue(a, key);
  const vb = sortValue(b, key);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const c = cmpValues(va, vb);
  return dir === 'asc' ? c : -c;
}

export function compareRows(sort: SortState) {
  return (a: RecordingActivityRow, b: RecordingActivityRow): number => {
    const primary = cmpKey(a, b, sort.key, sort.dir);
    if (primary !== 0) return primary;
    // Tie-breaks: the Last audio column's displayed value desc (the default secondary), then name.
    if (sort.key !== 'last_audio_age_seconds') {
      const t = cmpKey(a, b, 'last_audio_age_seconds', 'desc');
      if (t !== 0) return t;
    }
    return a.server_name.localeCompare(b.server_name);
  };
}

export function sortRows(rows: RecordingActivityRow[], sort: SortState): RecordingActivityRow[] {
  return [...rows].sort(compareRows(sort));
}

/** Click handler semantics: same key flips direction; a new key starts ascending. */
export function nextSort(current: SortState, key: SortKey): SortState {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: 'asc' };
}

// ----- Formatting -----

export const MISSING = '—';

const isNum = (v: number | null | undefined): v is number =>
  v !== null && v !== undefined && Number.isFinite(v);

/** Age in seconds → `8s`, `12m`, `2h 14m`, `4d 7h` (deviceHealth `formatSeconds`). null → `—`. */
export function formatAge(sec: number | null): string {
  return isNum(sec) ? formatSeconds(sec) : MISSING;
}

/**
 * A span (shift length, audio received) → same vocabulary, except an exact 0
 * renders `0m`: 0 is a measurement, not a missing value (design/NOTES.md).
 */
export function formatSpan(sec: number | null): string {
  if (!isNum(sec)) return MISSING;
  return sec <= 0 ? '0m' : formatSeconds(sec);
}

/** r2: closed-row gap → `1m before end`; null → `—`. */
export function formatBeforeEnd(sec: number | null): string {
  return isNum(sec) ? `${formatSeconds(sec)} before end` : MISSING;
}

/** The Last X cell text: `{gap} before end` for closed rows, the age for open rows. */
export function formatLast(r: RecordingActivityRow, kind: LastKind): string {
  const v = lastValue(r, kind);
  return isClosedForDisplay(r) ? formatBeforeEnd(v) : formatAge(v);
}

/** Battery passes through as the phone sent it: ≤ 1 is a fraction (×100), otherwise already %. */
export function formatBattery(v: number | null): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return MISSING;
  const pct = v <= 1 ? v * 100 : v;
  return `${Math.round(pct)}%`;
}

export function formatCount(v: number | null): string {
  return v === null || v === undefined ? MISSING : String(v);
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Location-local ISO (no offset) → `07:14` (24h), or `Thu 07:14` when the date is
 * not `todayLocal` (YYYY-MM-DD). Parsed by hand so no timezone conversion happens.
 * The 7-day window means a weekday is unambiguous.
 */
export function formatLocalTime(iso: string | null, todayLocal?: string): string {
  if (!iso) return MISSING;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return iso;
  const [, y, mo, d, hh, mm] = m;
  const time = `${hh}:${mm}`;
  if (todayLocal && `${y}-${mo}-${d}` === todayLocal) return time;
  const dow = new Date(Date.UTC(+y, +mo - 1, +d)).getUTCDay();
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow]} ${time}`;
}

/** Threshold seconds → whole minutes for the key (`5m`, `30m`); built from the response. */
export function formatThreshold(sec: number): string {
  return formatSeconds(sec);
}

/** HTTP status out of an `API <path> <status>: …` error, or null. */
export function httpStatusOf(message: string): number | null {
  const m = message.match(/ (\d{3}):/);
  return m ? parseInt(m[1], 10) : null;
}

/** The browser's local calendar date — used only to decide whether to print the day. */
export function todayLocalDate(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

export function formatDevice(r: Pick<RecordingActivityRow, 'device_model' | 'app_version' | 'patch_number'>): string {
  const parts: string[] = [];
  if (r.device_model) parts.push(r.device_model);
  if (r.app_version) parts.push(r.app_version);
  if (r.patch_number !== null && r.patch_number !== undefined) parts.push(`p${r.patch_number}`);
  return parts.length ? parts.join(' · ') : MISSING;
}

// ---------- Fake data ----------

function fakeRecordingActivity(tenantId: string): RecordingActivityResponse {
  const today = todayLocalDate();
  const base = {
    shift_ended_local: null,
    is_open: true,
    end_reason: null,
    device_model: 'G9T',
    app_version: '1.0.20+1',
    patch_number: 32,
    status_reason: null,
    last_activity_local: null,
    last_audio_before_end_seconds: null,
    last_transcript_before_end_seconds: null,
    last_heartbeat_before_end_seconds: null,
  };
  const rows: RecordingActivityRow[] = [
    {
      ...base,
      location_id: 'loc-ucsf', location_name: 'UCSF', server_id: 's1', server_name: 'Jane D.', shift_id: 'sh1',
      shift_started_local: `${today}T07:14:02`, shift_length_seconds: 3600, audio_seconds: 3420.5,
      transcript_part_count: 212, last_audio_age_seconds: 8, last_transcript_age_seconds: 45,
      last_heartbeat_age_seconds: 31, mic_route: 'usb_headset', battery_level: 0.64, ws_reconnect_count: 0,
      status: 'green',
    },
    {
      ...base,
      location_id: 'loc-ucsf', location_name: 'UCSF', server_id: 's2', server_name: 'Marco R.', shift_id: 'sh2',
      shift_started_local: `${today}T07:02:40`, shift_length_seconds: 4320, audio_seconds: 2580,
      transcript_part_count: 96, last_audio_age_seconds: 732, last_transcript_age_seconds: 1490,
      last_heartbeat_age_seconds: 705, mic_route: 'usb_headset', battery_level: 0.12, ws_reconnect_count: 4,
      status: 'red', status_reason: 'No audio for 12 min',
    },
    {
      ...base,
      location_id: 'loc-ucsf', location_name: 'UCSF', server_id: 's3', server_name: 'Priya K.', shift_id: 'sh3',
      shift_started_local: `${today}T08:30:00`, shift_length_seconds: 1500, audio_seconds: 1440,
      transcript_part_count: 31, last_audio_age_seconds: 6, last_transcript_age_seconds: 120,
      last_heartbeat_age_seconds: 22, mic_route: 'builtin_mic', battery_level: 81, ws_reconnect_count: 1,
      status: 'amber', status_reason: 'Built-in mic (USB mic not routed)',
    },
    {
      ...base,
      location_id: 'loc-ucsf', location_name: 'UCSF', server_id: 's4', server_name: 'Leo T.', shift_id: 'sh4',
      shift_started_local: `${today}T08:50:11`, shift_length_seconds: 420, audio_seconds: 0,
      transcript_part_count: 0, last_audio_age_seconds: null, last_transcript_age_seconds: null,
      last_heartbeat_age_seconds: null, mic_route: null, battery_level: null, ws_reconnect_count: null,
      device_model: null, app_version: null, patch_number: null,
      status: 'red', status_reason: 'No heartbeat yet',
    },
    {
      ...base,
      location_id: 'loc-ucsf', location_name: 'UCSF', server_id: 's5', server_name: 'Ana P.', shift_id: 'sh5',
      shift_started_local: '2026-10-22T10:01:00', shift_ended_local: '2026-10-22T14:03:12', is_open: false,
      end_reason: 'grace_expired', shift_length_seconds: 14532, audio_seconds: 13200, transcript_part_count: 640,
      last_audio_age_seconds: 151200, last_transcript_age_seconds: 151260, last_heartbeat_age_seconds: 151190,
      mic_route: 'usb_headset', battery_level: 0.33, ws_reconnect_count: 2,
      last_activity_local: '2026-10-22T14:03:00',
      last_audio_before_end_seconds: 72, last_transcript_before_end_seconds: 12, last_heartbeat_before_end_seconds: 82,
      status: 'ended',
    },
    {
      // r2: never tapped "End shift" — open in the DB, quiet for days.
      ...base,
      location_id: 'loc-ucsf', location_name: 'UCSF', server_id: 's8', server_name: 'Diego M.', shift_id: 'sh8',
      shift_started_local: '2026-10-22T07:05:00', shift_length_seconds: 11220, audio_seconds: 10980,
      transcript_part_count: 512, last_audio_age_seconds: 208920, last_transcript_age_seconds: 209040,
      last_heartbeat_age_seconds: 208860, mic_route: 'usb_headset', battery_level: 0.21, ws_reconnect_count: 1,
      last_activity_local: '2026-10-22T10:12:00',
      last_audio_before_end_seconds: 60, last_transcript_before_end_seconds: 180, last_heartbeat_before_end_seconds: 0,
      status: 'never_ended', status_reason: 'Went quiet Thu 10:12',
    },
    {
      ...base,
      location_id: 'loc-haas', location_name: 'Haas', server_id: 's6', server_name: 'Sam W.', shift_id: 'sh6',
      shift_started_local: `${today}T08:15:00`, shift_length_seconds: 2700, audio_seconds: 2640,
      transcript_part_count: 4, last_audio_age_seconds: 9, last_transcript_age_seconds: 1920,
      last_heartbeat_age_seconds: 40, mic_route: 'usb_device', battery_level: 0.9, ws_reconnect_count: 0,
      status: 'amber', status_reason: 'No transcript for 32 min',
    },
    {
      ...base,
      location_id: 'loc-haas', location_name: 'Haas', server_id: 's7', server_name: 'Kim L.', shift_id: 'sh7',
      shift_started_local: `${today}T06:00:00`, shift_ended_local: `${today}T08:00:00`, is_open: false,
      end_reason: null, shift_length_seconds: 7200, audio_seconds: 6900, transcript_part_count: 300,
      last_audio_age_seconds: 3700, last_transcript_age_seconds: 3720, last_heartbeat_age_seconds: 3650,
      mic_route: 'usb_headset', battery_level: 0.5, ws_reconnect_count: 0,
      last_activity_local: `${today}T07:58:20`,
      // Recording died ~40 min before the shift was closed.
      last_audio_before_end_seconds: 2400, last_transcript_before_end_seconds: 2460, last_heartbeat_before_end_seconds: null,
      status: 'ended',
    },
  ];
  return {
    generated_at: new Date().toISOString(),
    tenant_id: tenantId,
    window_days: 7,
    thresholds: {
      red_no_audio_seconds: 300,
      red_no_heartbeat_seconds: 300,
      amber_no_transcript_seconds: 1800,
      never_ended_after_seconds: 7200,
    },
    rows,
  };
}
